 /* eslint-disable no-undef */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Plus, Info, Calendar, Upload, Eye, Settings, Edit, Trash2, Download, MoreVertical, DollarSign, MessageCircle } from 'lucide-react';
import Select from 'react-select';
import { useReactToPrint } from 'react-to-print';
import { obtenerClientes, obtenerSeries, crearFactura, obtenerFacturas, obtenerSiguienteNumero, obtenerConfiguracionEmpresa, obtenerClienteDetalle, obtenerFacturaDetalle, descargarFacturaPdf, obtenerMaterialesAviso } from '../servicios/api';
import ModalCrearCliente from '../componentes/ModalCrearCliente';
import { normalizarIdentificacionFiscal } from '../validarIdentificacionFiscal';
import ModalGestionarNotas from '../componentes/ModalGestionarNotas'; // Importar el nuevo modal de notas
import CrearSerieNumeracion from '../componentes/CrearSerieNumeracion';
import ModalConfigurarFacturas from '../componentes/ModalConfigurarFacturas';
import ModalGestionCobros from '../componentes/ModalGestionCobros';
import Portal from '../componentes/Portal'; // Import the Portal component

  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: '#334155', // slate-700
      borderColor: '#475569', // slate-600
      color: '#e2e8f0', // slate-200
      minHeight: '42px',
      boxShadow: 'none',
      fontSize: '14px',
      width: '100%', // Ensure it takes full width within its container
    }),
    singleValue: (provided) => ({ ...provided, color: '#e2e8f0', fontSize: '14px' }), // slate-200
    menu: (provided) => ({ ...provided, backgroundColor: '#334155' }), // slate-700
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#60a5fa' : '#334155', // blue-500, blue-400, slate-700
      color: state.data.isFixed ? '#94a3b8' : '#e2e8f0', // slate-400 for fixed options
      fontSize: '14px',
      cursor: state.data.isFixed ? 'not-allowed' : 'pointer', // Disable cursor for fixed options
    }),
  };

const InvoiceForm = () => {
  console.log('FacturacionSimple (InvoiceForm) is rendering.');
  const location = useLocation();
  const navigate = useNavigate();
  const borradorAviso = location.state?.borradorFactura;
  const [invoiceData, setInvoiceData] = useState({
    number: '', // Se obtendrá de la API
    date: new Date().toISOString().slice(0, 10),
    dueDate: '',
    paymentTerms: '', // No está en el formulario actual, se puede omitir o añadir
    incomeType: '705', // Valor por defecto
    client: null, // Objeto cliente seleccionado
    privateNotes: '',
    retention: '0', // Porcentaje de retención
    invoiceNotes: '',
    language: 'Idioma por defecto del cliente',
    numberFormat: 'Formato por defecto del cliente',
    markAsPaid: false,
    concepts: [{
      id: Date.now(), // Add unique ID to initial concept
      description: '',
      detailedDescription: '', // New field for detailed description
      taxableBase: '',
      quantity: 1,
      discount: 0,
      vat: 21
    }]
  });

  const [series, setSeries] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [lastInvoices, setLastInvoices] = useState([]);
  const [selectedSerie, setSelectedSerie] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [isModalCrearClienteOpen, setIsModalCrearClienteOpen] = useState(false);
  const [isModalNotasOpen, setIsModalNotasOpen] = useState(false); // Estado para el modal de notas
  const [isModalCrearSerieOpen, setIsModalCrearSerieOpen] = useState(false);
  const [isModalConfigurarFacturasOpen, setIsModalConfigurarFacturasOpen] = useState(false);
  const [isModalGestionCobrosOpen, setIsModalGestionCobrosOpen] = useState(false);
  const [selectedInvoiceIdForCobros, setSelectedInvoiceIdForCobros] = useState(null);
  const [isAdvancedEditing, setIsAdvancedEditing] = useState(false); // State for advanced editing
  const [openDropdownId, setOpenDropdownId] = useState(null); // State to manage which dropdown is open
  const [openInvoiceDropdownId, setOpenInvoiceDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 }); // Position for the portal dropdown
  const dropdownButtonRef = useRef(null); // Ref for the dropdown trigger button
  const dropdownRef = useRef(null);
  const avisoPrefillRef = useRef(null);
  const [avisoFacturaId, setAvisoFacturaId] = useState(null);
  const [avisoPrefillError, setAvisoPrefillError] = useState('');
  

  const toggleDropdown = (id) => {
    if (openDropdownId === id) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(id);
      if (dropdownButtonRef.current) {
        const rect = dropdownButtonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX - 192 + rect.width // Adjust for dropdown width (w-48 = 192px)
        });
      }
    }
  };

  const toggleInvoiceDropdown = (invoiceId, event) => {
    if (openInvoiceDropdownId === invoiceId) {
      setOpenInvoiceDropdownId(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX - 224 + rect.width // w-56 is 224px
      });
      setOpenInvoiceDropdownId(invoiceId);
    }
  };

  const moveConcepto = (id, direction) => {
    const currentIndex = invoiceData.concepts.findIndex(conc => conc.id === id);
    if (currentIndex === -1) return;

    const newConcepts = [...invoiceData.concepts];
    const [movedConcepto] = newConcepts.splice(currentIndex, 1);
    newConcepts.splice(direction === 'up' ? currentIndex - 1 : currentIndex + 1, 0, movedConcepto);
    setInvoiceData(prev => ({ ...prev, concepts: newConcepts }));
  };

  const handleSerieCreada = (newSerie) => {
    setIsModalCrearSerieOpen(false);
    if (newSerie && (newSerie.id ?? newSerie.id_serie) && newSerie.nombre) {
      const option = {
        value: newSerie.id ?? newSerie.id_serie,
        label: `Serie: ${newSerie.nombre}`
      };
      setSeries(prev => [...prev.filter(serie => String(serie.value) !== String(option.value)), option]);
      setSelectedSerie(option);
      getNextInvoiceNumber(option);
    } else {
      loadInitialData();
    }
  };

  const handleClienteCreado = (newClient) => {
    const normalizedClient = {
      id: newClient?.id_cliente ?? newClient?.id ?? null,
      id_cliente: newClient?.id_cliente ?? newClient?.id ?? null,
      nombre: newClient?.nombre_cliente ?? newClient?.nombre ?? 'Cliente',
      nombre_cliente: newClient?.nombre_cliente ?? newClient?.nombre ?? 'Cliente',
      nif: normalizarIdentificacionFiscal(newClient?.nif ?? newClient?.nif_cif),
      nif_cif: normalizarIdentificacionFiscal(newClient?.nif_cif ?? newClient?.nif),
      telefono: newClient?.telefono ?? '',
      direccion: newClient?.direccion ?? ''
    };

    const newOption = {
      value: normalizedClient.id_cliente,
      label: `${normalizedClient.nombre_cliente} (${normalizedClient.nif || 'N/A'})`,
      ...normalizedClient
    };

    setClientes(prev => {
      const existing = prev.some(option => Number(option.value) === Number(normalizedClient.id_cliente));
      return existing ? prev : [...prev, newOption];
    });
    setSelectedClient(newOption);
    setIsModalCrearClienteOpen(false);
  };

  const handleSaveNotes = (notes) => {
    setInvoiceData(prev => ({ ...prev, invoiceNotes: notes }));
    
    setIsModalNotasOpen(false);
  };

  const retentionOptions = [
    { value: '0', label: 'Sin Retención', disabled: false },
    { 
      label: 'Valores Actuales', 
      disabled: true,
      isGroup: true
    },
    { value: '1', label: '1% - Módulos o actividades Ganaderas', disabled: false },
    { value: '2', label: '2% - Sector Agrario', disabled: false },
    { value: '7', label: '7% - Profesionales en los dos primeros años de actividad', disabled: false },
    { value: '15', label: '15% - Profesionales', disabled: false },
    { value: '19', label: '19% - Alquileres o Intereses (Capital Mobiliario)', disabled: false },
    { 
      label: 'Valores Modificados (Ceuta y Melilla)', 
      disabled: true,
      isGroup: true
    },
    { value: '2.8', label: '2,8% - Profesionales en los dos primeros años (Ceuta Melilla)', disabled: false },
    { value: '6', label: '6% - Profesionales (Ceuta y Melilla)', disabled: false },
    { value: '7.6', label: '7,6% - Alquiler (Ceuta y Melilla)', disabled: false },
    { 
      label: 'Valores Antiguos', 
      disabled: true,
      isGroup: true
    },
    { value: '3.5', label: '3,5% - Profesionales Ceuta y Melilla (antes 05/07/2018)', disabled: false },
    { value: '4.5', label: '4,5% - Profesionales Ceuta y Melilla (antes 12/07/2015)', disabled: false },
    { value: '7.5', label: '7,5% - Profesionales Ceuta y Melilla (antes 05/07/2018)', disabled: false },
    { value: '9', label: '9% - Profesionales en los dos primeros años (antes 12/07/2015)', disabled: false },
    { value: '9.5', label: '9,5% - Profesionales Ceuta y Melilla (antes 12/07/2018)', disabled: false },
    { value: '10.5', label: '10,5% - Profesionales Ceuta y Melilla (antes 2015)', disabled: false },
    { value: '19.5', label: '19,5% - Alquileres/Intereses (antes 2016)', disabled: false },
    { value: '20', label: '20% - Alquileres/Intereses (antes 12/07/2018)', disabled: false }
  ];

  const languageOptions = [
    'Idioma por defecto del cliente',
    'Español',
    'Catalán',
    'Inglés',
    'Gallego',
    'Alemán',
    'Francés',
    'Euskera'
  ];

  const incomeTypeOptions = [
    { value: '700', label: '700. Venta de Mercaderías' },
    { value: '705', label: '705. Prestación de servicios' },
    { value: '7098', label: '7098. Otras prestaciones de servicios' },
    { value: '7099', label: '7099. Otras ventas de mercaderías' },
    { value: '740', label: '740. Subvenciones a la explotación' },
    { value: '746', label: '746. Subvención al capital' },
    { value: '747', label: '747. Otras subvenciones' },
    { value: '752', label: '752. Ingresos por arrendamientos' },
    { value: '769', label: '769. Ingresos financieros' },
  ];

  const loadInitialData = async () => {
    console.log('loadInitialData: Starting data load...');
    try {
      const [resClientes, resSeries, resFacturas, resCompanyConfig] = await Promise.all([
        obtenerClientes(),
        obtenerSeries(),
        obtenerFacturas(), // Obtener las últimas facturas
        obtenerConfiguracionEmpresa()
      ]);
      console.log('loadInitialData: API calls completed.');

      if (resClientes.exito && Array.isArray(resClientes.clientes)) {
        console.log('loadInitialData: Clients data received.', resClientes.clientes.length);
        const clientesOptions = resClientes.clientes.map(c => ({
          value: c.id_cliente,
          label: `${c.nombre_cliente} (${normalizarIdentificacionFiscal(c.nif) || 'N/A'})`
        }));
        setClientes(clientesOptions);
      } else {
        console.error('loadInitialData: Error or no clients data received.', resClientes);
      }

      if (resSeries.exito && Array.isArray(resSeries.series)) {
        console.log('loadInitialData: Series data received.', resSeries.series.length);
        
        const uniqueSeries = Object.values(resSeries.series.reduce((acc, serie) => {
          if (serie.nombre) acc[serie.nombre] = serie;
          return acc;
        }, {}));

        const seriesOptions = uniqueSeries
          .filter(s => s.nombre !== undefined && s.nombre !== null)
          .map(s => ({
            value: s.id ?? s.id_serie,
            label: `Serie: ${s.nombre || 'Sin Nombre'}`
          }))
          .filter(s => s.value !== undefined && s.value !== null && s.value !== '');
        setSeries(seriesOptions);
        // La auto-selección de la serie se desactiva para diagnosticar el bucle infinito.
        // if (!selectedSerie || !seriesOptions.some(s => s.value === selectedSerie.value)) {
        //   if (seriesOptions.length > 0) {
        //     setSelectedSerie(seriesOptions[0]);
        //   } else {
        //     setSelectedSerie(null);
        //   }
        // }
      } else {
        console.error('loadInitialData: Error or no series data received.', resSeries);
      }

      if (resFacturas.exito && Array.isArray(resFacturas.facturas)) {
        console.log('loadInitialData: Invoices data received.', resFacturas.facturas.length);
        setLastInvoices(resFacturas.facturas.slice(0, 5)); // Mostrar solo las últimas 5
      } else {
        console.error('loadInitialData: Error or no invoices data received.', resFacturas);
      }

      if (resCompanyConfig.exito && resCompanyConfig.configuracion) {
        const config = resCompanyConfig.configuracion;
        if (config.logo_path) {
          let logoPath = config.logo_path.trim();
          if (logoPath.endsWith(':')) {
            logoPath = logoPath.slice(0, -1);
          }
          config.logo_path = logoPath;
        }
        setCompanyData(config);
      } else {
        console.error('loadInitialData: Error or no company config received.', resCompanyConfig);
      }
      console.log('loadInitialData: Data processing complete.');

    } catch (error) {
      console.error("loadInitialData: Caught error during data load:", error);
    }
  };

  // Cargar datos iniciales (clientes, series, últimas facturas)
  useEffect(() => {
    loadInitialData();
  }, []); // Dependencias vacías para que se ejecute solo una vez

  useEffect(() => {
    const avisoId = Number(borradorAviso?.idAviso);
    const clienteId = Number(borradorAviso?.idCliente);
    if (!avisoId || !clienteId || clientes.length === 0 || avisoPrefillRef.current === avisoId) return;

    avisoPrefillRef.current = avisoId;

    const aplicarBorradorAviso = async () => {
      try {
        let cliente = clientes.find(option => Number(option.value) === clienteId);
        if (!cliente) {
          const respuestaCliente = await obtenerClienteDetalle(clienteId);
          if (respuestaCliente.exito && respuestaCliente.cliente) {
            const detalle = respuestaCliente.cliente;
            cliente = {
              value: Number(detalle.id_cliente ?? clienteId),
              label: `${detalle.nombre_cliente ?? borradorAviso.nombreCliente ?? 'Cliente'} (${detalle.nif ?? 'N/A'})`,
              ...detalle
            };
          }
        }
        if (!cliente) throw new Error('No se pudo cargar el cliente asociado al aviso.');

        const respuestaMateriales = await obtenerMaterialesAviso(avisoId);
        if (!respuestaMateriales.exito || !Array.isArray(respuestaMateriales.materiales)) {
          throw new Error(respuestaMateriales.mensaje || 'No se pudieron cargar los conceptos del aviso.');
        }

        const conceptos = respuestaMateriales.materiales.map((material, index) => ({
          id: `aviso-${avisoId}-${material.id ?? index}`,
          description: material.concepto || `Concepto del aviso #${avisoId}`,
          detailedDescription: '',
          taxableBase: Number(material.precio_venta) || 0,
          quantity: Number(material.cantidad) || 1,
          discount: 0,
          vat: 21
        }));

        if (conceptos.length === 0) {
          const detalleReparacion = [borradorAviso.descripcion, borradorAviso.diagnostico]
            .filter(Boolean)
            .join(' - ');
          conceptos.push({
            id: `aviso-${avisoId}-reparacion`,
            description: detalleReparacion || `Reparación del aviso #${avisoId}`,
            detailedDescription: '',
            taxableBase: Number(borradorAviso.precioCobrado) || 0,
            quantity: 1,
            discount: 0,
            vat: 21
          });
        }

        setClientes(prev => prev.some(option => Number(option.value) === clienteId) ? prev : [...prev, cliente]);
        setSelectedClient(cliente);
        setInvoiceData(prev => ({ ...prev, client: cliente.value, concepts: conceptos }));
        setAvisoFacturaId(avisoId);
        setAvisoPrefillError('');
        navigate(location.pathname, { replace: true, state: null });
      } catch (error) {
        console.error('No se pudo preparar la factura desde el aviso:', error);
        setAvisoPrefillError(error.message || 'No se pudo preparar la factura desde el aviso.');
      }
    };

    aplicarBorradorAviso();
  }, [borradorAviso, clientes, location.pathname, navigate]);

  useEffect(() => {
    const fetchClientDetails = async () => {
      if (selectedClient && selectedClient.value) {
        try {
          const response = await obtenerClienteDetalle(selectedClient.value);
          if (response.exito && response.cliente) {
            setClientDetails(response.cliente);
          } else {
            console.error("Error al obtener detalles del cliente:", response);
            setClientDetails(null);
          }
        } catch (error) {
          console.error("Error de conexión al obtener detalles del cliente:", error);
          setClientDetails(null);
        }
      } else {
        setClientDetails(null);
      }
    };
    fetchClientDetails();
  }, [selectedClient]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Ignore clicks on the trigger buttons
      if (event.target.closest('.invoice-dropdown-trigger') || event.target.closest('.concept-dropdown-trigger')) {
        return;
      }
      // Close dropdown if click is outside
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenInvoiceDropdownId(null);
        setOpenDropdownId(null); // Also close the concepts dropdown
      }
    };

    if (openInvoiceDropdownId !== null || openDropdownId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openInvoiceDropdownId, openDropdownId]);

  const handleInputChange = (field, value) => {
    setInvoiceData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConceptChange = (index, field, value) => {
    const newConcepts = [...invoiceData.concepts];
    newConcepts[index][field] = value;
    setInvoiceData(prev => ({
      ...prev,
      concepts: newConcepts
    }));
  };

  const addConcept = () => {
    setInvoiceData(prev => ({
      ...prev,
      concepts: [...prev.concepts, {
        id: Date.now(), // Assign a unique ID
        description: '',
        detailedDescription: '', // New field for detailed description
        taxableBase: '',
        quantity: 1,
        discount: 0,
        vat: 21
      }]
    }));
  };

  const deleteConcept = (index) => {
    if (invoiceData.concepts.length > 1) {
      setInvoiceData(prev => ({
        ...prev,
        concepts: prev.concepts.filter((_, i) => i !== index)
      }));
    } else {
      alert('Debe haber al menos un concepto.');
    }
  };

  const duplicateConcept = (id) => {
    const conceptToDuplicate = invoiceData.concepts.find(c => c.id === id);
    if (conceptToDuplicate) {
      setInvoiceData(prev => ({
        ...prev,
        concepts: [...prev.concepts, { ...conceptToDuplicate, id: Date.now() }]
      }));
    }
  };

  const handleSeriesChange = (e) => {
    const { value } = e.target;
    if (value === 'add_new_serie') {
      setIsModalCrearSerieOpen(true);
      e.target.value = selectedSerie ? selectedSerie.value : '';
    } else if (!value) {
      setSelectedSerie(null);
      setInvoiceData(prev => ({ ...prev, number: '' }));
    } else {
      const newSelectedSerie = series.find(s => String(s.value) === value)
        || {
          value,
          label: e.target.selectedOptions[0]?.textContent || `Serie ${value}`
        };
      setSelectedSerie(newSelectedSerie);
      getNextInvoiceNumber(newSelectedSerie);
    }
  };

  const getNextInvoiceNumber = async (serie = selectedSerie) => {
    const serieId = Number(serie?.value ?? serie?.id ?? serie?.id_serie);
    if (!Number.isInteger(serieId) || serieId <= 0) {
      alert('Por favor, selecciona una serie primero.');
      return;
    }
    try {
      const response = await obtenerSiguienteNumero(serieId);
      if (response.exito) {
        setInvoiceData(prev => ({ ...prev, number: response.siguiente_numero }));
      } else {
        alert(`Error al obtener el siguiente número: ${response.mensaje}`);
      }
    } catch (error) {
      console.error("Error al obtener el siguiente número:", error);
      const mensajeServidor = error.response?.data?.mensaje;
      alert(mensajeServidor
        ? `No se pudo obtener el siguiente número: ${mensajeServidor}`
        : 'Error de conexión al obtener el siguiente número. Comprueba la conexión e inténtalo de nuevo.');
    }
  };

  const invoiceNumberPreview = React.useMemo(() => {
    const serieLabel = selectedSerie?.label || 'SERIE';
    const numero = invoiceData.number || '0000';
    return `${serieLabel}-${numero}`;
  }, [selectedSerie, invoiceData.number]);

  const getWhatsappPhone = () => {
    const rawPhone = clientDetails?.telefono || selectedClient?.telefono || '';
    const digits = String(rawPhone).replace(/\D/g, '');
    if (!digits) return null;
    if (digits.startsWith('34')) return digits;
    if (digits.startsWith('6') || digits.startsWith('7')) return `34${digits}`;
    return digits;
  };

  const handleSendInvoiceByWhatsapp = async (invoice) => {
    const phoneDigits = String(invoice?.telefono_cliente || getWhatsappPhone() || '').replace(/\D/g, '');
    const phone = phoneDigits.startsWith('34')
      ? phoneDigits
      : /^[67]\d{8}$/.test(phoneDigits) ? `34${phoneDigits}` : phoneDigits;
    if (!phone) {
      alert('Este cliente no tiene teléfono registrado para enviar la factura por WhatsApp.');
      return;
    }

    const nombreCliente = invoice?.nombre_cliente || selectedClient?.label?.split('(')[0]?.trim() || 'cliente';
    const numeroFactura = invoice?.numero_factura || invoiceNumberPreview;
    const totalFactura = Number(invoice?.total ?? total).toFixed(2);
    const texto = encodeURIComponent(
      `Hola ${nombreCliente}, te comparto la factura ${numeroFactura} por un importe total de ${totalFactura} €. Muchas gracias.`
    );
    const whatsappUrl = `https://wa.me/${phone}?text=${texto}`;
    const whatsappWindow = window.open('about:blank', '_blank');
    let pdfBlob = null;
    let file = null;

    try {
      if (!invoice?.id_factura) {
        if (whatsappWindow) whatsappWindow.location.href = whatsappUrl;
        else alert('Guarda primero la factura para poder compartir su PDF por WhatsApp.');
        return;
      }

      pdfBlob = await descargarFacturaPdf(invoice.id_factura);
      file = new File([pdfBlob], `Factura_${String(numeroFactura).replace(/[^A-Za-z0-9_-]/g, '_')}.pdf`, {
        type: 'application/pdf'
      });

      if (navigator.canShare?.({ files: [file] }) && navigator.share) {
        try {
          await navigator.share({
            files: [file],
            title: `Factura ${numeroFactura}`,
            text: `Hola ${nombreCliente}, te comparto la factura ${numeroFactura} por un importe total de ${totalFactura} €.`
          });
          whatsappWindow?.close();
          return;
        } catch (shareError) {
          if (shareError.name === 'AbortError') {
            whatsappWindow?.close();
            return;
          }
          console.warn('No se pudo abrir el menú de compartir archivos; se usará la descarga manual.', shareError);
        }
      }

      const downloadUrl = URL.createObjectURL(pdfBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.download = file.name;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 60_000);

      if (whatsappWindow) {
        whatsappWindow.location.href = whatsappUrl;
        alert('El PDF se ha descargado. Adjunta el archivo descargado en el chat de WhatsApp.');
      } else {
        alert('El PDF se ha descargado. Abre WhatsApp y adjúntalo al chat del cliente.');
      }
    } catch (error) {
      whatsappWindow?.close();
      if (error.name === 'AbortError') return;
      console.error('Error al preparar el envío de la factura por WhatsApp:', error);
      alert(pdfBlob ? 'No se pudo descargar el PDF para WhatsApp.' : 'No se pudo preparar el PDF para WhatsApp. Comprueba la conexión e inténtalo de nuevo.');
    }
  };

  const handleCreateInvoice = async () => {
    const clienteId = [selectedClient?.value, selectedClient?.id_cliente, selectedClient?.id]
      .find(value => value !== undefined && value !== null && String(value).trim() !== '');
    const serieId = [selectedSerie?.value, selectedSerie?.id, selectedSerie?.id_serie]
      .find(value => value !== undefined && value !== null && String(value).trim() !== '');
    const numeroFactura = String(invoiceData.number ?? '').trim();
    const conceptos = Array.isArray(invoiceData.concepts) ? invoiceData.concepts : [];
    const camposPendientes = [];

    if (clienteId === undefined || clienteId === null || clienteId === '') {
      camposPendientes.push('Cliente');
    }
    if (serieId === undefined || serieId === null || serieId === '') {
      camposPendientes.push('Serie');
    }
    if (!numeroFactura) {
      camposPendientes.push('Número de Factura');
    }
    if (conceptos.length === 0) {
      camposPendientes.push('al menos un concepto');
    }

    if (camposPendientes.length > 0) {
      console.warn('No se puede crear la factura; faltan datos requeridos.', {
        selectedSerie,
        seriesDisponibles: series.map(serie => ({ value: serie.value, label: serie.label })),
        clienteId,
        serieId,
        numeroFactura,
        conceptos: conceptos.length
      });
      alert(`No se puede crear la factura. Revisa: ${camposPendientes.join(', ')}.`);
      return;
    }

    const facturaData = {
      clienteId,
      serieId,
      ...(avisoFacturaId ? { avisoId: avisoFacturaId } : {}),
      numeroFactura,
      fechaFactura: invoiceData.date,
      fechaVencimiento: invoiceData.dueDate,
      subtotal: subtotal,
      totalIva: vatAmount,
      retencion: retentionAmount, // El valor calculado de la retención
      totalFinal: total,
      conceptos: conceptos.map(c => ({
        descripcion: c.description,
        descripcionDetallada: '', // No está en el formulario
        cantidad: c.quantity,
        baseImponible: c.taxableBase,
        descuento: c.discount,
        ivaPorcentaje: c.vat
      }))
    };

    try {
      const response = await crearFactura(facturaData);
      if (response.exito) {
        if (avisoFacturaId && Number(response.notificaciones_pendientes_eliminadas) > 0) {
          window.dispatchEvent(new Event('notificaciones-actualizadas'));
        }
        let listaActualizada = false;
        try {
          const facturasActualizadas = await obtenerFacturas();
          if (!facturasActualizadas.exito || !Array.isArray(facturasActualizadas.facturas)) {
            throw new Error(facturasActualizadas.mensaje || 'Respuesta inválida al obtener facturas.');
          }
          setLastInvoices(facturasActualizadas.facturas.slice(0, 5));
          listaActualizada = true;
        } catch (errorActualizacion) {
          console.error('La factura se creó, pero no se pudo actualizar la lista:', errorActualizacion);
        }
        alert(listaActualizada
          ? 'Factura creada exitosamente.'
          : 'Factura creada, pero no se pudo actualizar el listado. Recarga la página para verla.');
        setInvoiceData({
          number: '',
          date: new Date().toISOString().slice(0, 10),
          dueDate: '',
          paymentTerms: '',
          incomeType: '705',
          client: null,
          privateNotes: '',
          retention: '0',
          invoiceNotes: '',
          language: 'Idioma por defecto del cliente',
          numberFormat: 'Formato por defecto del cliente',
          markAsPaid: false,
          concepts: [{
            description: '',
            taxableBase: 0,
            quantity: 1,
            discount: 0,
            vat: 21
          }]
        });
        setSelectedClient(null);
        setSelectedSerie(series.length > 0 ? series[0] : null);
        setAvisoFacturaId(null);
      } else {
        alert(`Error al crear la factura: ${response.mensaje}`);
      }
    } catch (error) {
      console.error("Error al enviar la factura:", error);
      const mensajeServidor = error.response?.data?.mensaje;
      alert(mensajeServidor
        ? `No se pudo crear la factura: ${mensajeServidor}`
        : 'Error de conexión al crear la factura. Comprueba la conexión e inténtalo de nuevo.');
    }
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      const pdfBlob = await descargarFacturaPdf(invoiceId);
      const downloadUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `Factura_${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 60_000);
    } catch (error) {
      console.error("Error al descargar el PDF de la factura:", error);
      const mensaje = error.response?.data?.mensaje;
      alert(mensaje ? `No se pudo descargar el PDF: ${mensaje}` : 'Error de conexión al descargar el PDF de la factura.');
    }
  };

  const handleEmitirFacturaRectificativa = async (invoice) => {
    if (!invoice) return;

    const motivo = window.prompt('Indica el motivo de la factura rectificativa (anulación, abono o corrección):', 'Anulación por devolución del cliente');
    if (!motivo || !motivo.trim()) {
      return;
    }

    const clienteId = invoice.id_cliente_fk;
    const serieParaRectificacion = series.find(serie => Number(serie.value) === Number(invoice.serie_id));
    if (!clienteId || !serieParaRectificacion) {
      alert('No se pudo identificar el cliente original o una serie para la rectificación.');
      return;
    }

    let conceptosRectificacion;
    try {
      const respuestaDetalle = await obtenerFacturaDetalle(invoice.id_factura);
      const conceptosOriginales = respuestaDetalle?.factura?.items;
      if (!respuestaDetalle.exito || !Array.isArray(conceptosOriginales) || conceptosOriginales.length === 0) {
        alert('No se pudieron recuperar los conceptos de la factura original para rectificarla.');
        return;
      }
      conceptosRectificacion = conceptosOriginales.map(item => {
        const base = Math.abs(Number(item.pricePerUnit || 0));
        const cantidad = Number(item.units || 0);
        const totalLinea = Math.abs(Number(item.total || 0));
        const descuento = base * cantidad > 0
          ? Math.max(0, Math.min(100, (1 - totalLinea / (base * cantidad)) * 100))
          : 0;
        return {
          descripcion: item.concept || `Rectificación de ${invoice.numero_factura}`,
          descripcionDetallada: item.description || '',
          cantidad,
          baseImponible: -base,
          descuento,
          ivaPorcentaje: Number(item.ivaPercentage || 0),
        };
      });
    } catch (error) {
      console.error('Error al recuperar los conceptos de la factura original:', error);
      alert('No se pudieron recuperar los conceptos de la factura original.');
      return;
    }

    let numeroRectificativa;
    try {
      const respuestaNumero = await obtenerSiguienteNumero(serieParaRectificacion.value);
      if (!respuestaNumero.exito || !respuestaNumero.siguiente_numero) {
        alert(`No se pudo obtener el siguiente número de factura: ${respuestaNumero.mensaje || 'respuesta inválida'}`);
        return;
      }
      numeroRectificativa = respuestaNumero.siguiente_numero;
    } catch (error) {
      console.error('Error al obtener el número de la factura rectificativa:', error);
      alert('No se pudo obtener el siguiente número de factura. Inténtalo de nuevo.');
      return;
    }

    const facturaData = {
      clienteId,
      serieId: serieParaRectificacion.value,
      numeroFactura: numeroRectificativa,
      fechaFactura: new Date().toISOString().slice(0, 10),
      fechaVencimiento: '',
      subtotal: -Math.abs(Number(invoice.base_imponible || 0)),
      totalIva: -Math.abs(Number(invoice.iva || 0)),
      retencion: -Math.abs(Number(invoice.retencion || 0)),
      totalFinal: -Math.abs(Number(invoice.total || 0)),
      tipoFactura: 'rectificativa',
      facturaRectificadaId: invoice.id_factura,
      motivoRectificacion: motivo.trim(),
      conceptos: conceptosRectificacion,
    };

    try {
      const response = await crearFactura(facturaData);
      if (response.exito) {
        alert('Factura rectificativa emitida correctamente.');
        loadInitialData();
      } else {
        alert(`No se pudo emitir la factura rectificativa: ${response.mensaje}`);
      }
    } catch (error) {
      console.error('Error al emitir factura rectificativa:', error);
      alert('Error al emitir la factura rectificativa.');
    }
  };

  const subtotal = invoiceData.concepts.reduce((sum, concept) => {
    return sum + (concept.taxableBase * concept.quantity * (1 - concept.discount / 100));
  }, 0);

  const vatAmount = invoiceData.concepts.reduce((sum, concept) => {
    return sum + (concept.taxableBase * concept.quantity * (1 - concept.discount / 100) * concept.vat / 100);
  }, 0);

  const retentionAmount = invoiceData.retention !== '0' ? 
    (subtotal * parseFloat(invoiceData.retention) / 100) : 0;

  const total = subtotal + vatAmount - retentionAmount;

  return (
    <div className="max-w-6xl mx-auto bg-slate-900 text-gray-300 p-6 rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 border-b border-slate-700 pb-4">
        <div className="flex items-center space-x-4">
          <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
            <span className="text-white text-sm font-bold">B</span>
          </div>
          <h1 className="text-xl font-semibold text-white">Nueva factura emitida</h1>
        </div>
        <div className="text-sm text-gray-400">
          Está trabajando en el período: 
          <select className="ml-2 border border-slate-600 rounded px-2 py-1 bg-slate-700 text-gray-300">
            <option>2025-3T</option>
          </select>
        </div>
      </div>

      {avisoFacturaId && (
        <div className="mb-6 rounded-lg border border-emerald-700 bg-emerald-950/40 p-4 text-sm text-emerald-100">
          Factura preparada desde el aviso #{avisoFacturaId}. Revisa los conceptos, los precios y el IVA antes de emitirla.
        </div>
      )}
      {avisoPrefillError && (
        <div role="alert" className="mb-6 rounded-lg border border-red-700 bg-red-950/40 p-4 text-sm text-red-200">
          {avisoPrefillError}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3 mb-6">
        <button onClick={() => setIsModalConfigurarFacturasOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center space-x-2 text-sm">
          <Settings size={16} />
          <span>Configurar factura</span>
        </button>
        <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center space-x-2 text-sm">
          <Info size={16} />
          <span>Ayuda</span>
        </button>
      </div>

      {/* Invoice Information */}
      <div className="bg-slate-800 p-6 rounded-lg mb-6 border border-slate-700">
        <h2 className="text-lg font-semibold text-blue-400 mb-4 border-b border-slate-700 pb-2">Información de la factura</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            {/* Invoice Number */}
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-2 md:space-y-0">
              <label className="text-sm font-medium text-gray-400 md:w-24 md:text-right">Serie y nº</label>
              <div className="flex flex-col sm:flex-row flex-1 gap-2">
                <select 
                  className="border border-slate-600 rounded px-2 py-2 text-sm bg-slate-700 text-gray-300 w-full sm:w-40"
                  value={selectedSerie?.value || ''}
                  onChange={handleSeriesChange}
                >
                  <option value="">Serie</option>
                  {series.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  <option value="add_new_serie" style={{color: '#60a5fa', fontWeight: 'bold'}}>
                    + Nueva Serie...
                  </option>
                </select>
                <input
                  type="text"
                  value={invoiceData.number}
                  onChange={(e) => handleInputChange('number', e.target.value)}
                  className="border border-slate-600 rounded px-2 py-2 text-sm bg-slate-700 text-gray-300 w-full sm:w-32"
                  placeholder="0001"
                />
                <div className="flex items-center justify-center rounded border border-blue-500/40 bg-blue-500/10 px-3 py-2 text-xs text-blue-200 font-semibold min-w-[120px]">
                  {invoiceNumberPreview}
                </div>
                <button type="button" onClick={() => getNextInvoiceNumber()} className="text-blue-400 text-sm whitespace-nowrap hover:underline self-center">Siguiente</button>
              </div>
            </div>

            {/* Date */}
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-2 md:space-y-0">
              <label className="text-sm font-medium text-gray-400 md:w-24 md:text-right">Fecha</label>
              <div className="flex flex-1 items-center space-x-2">
                <input
                  type="date"
                  value={invoiceData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  className="flex-1 border border-slate-600 rounded px-2 py-1 text-sm bg-slate-700 text-gray-300"
                />
                <Calendar className="w-4 h-4 text-gray-400" />
              </div>
            </div>

            {/* Due Date */}
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-2 md:space-y-0">
              <label className="text-sm font-medium text-gray-400 md:w-24 md:text-right">Fecha de Vencimiento</label>
              <div className="flex flex-1 flex-col space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="date"
                    value={invoiceData.dueDate}
                    onChange={(e) => handleInputChange('dueDate', e.target.value)}
                    className="flex-1 border border-slate-600 rounded px-2 py-1 text-sm bg-slate-700 text-gray-300"
                  />
                  <Calendar className="w-4 h-4 text-gray-400" />
                </div>
                <div className="flex flex-wrap gap-2 text-sm">
                  <button onClick={() => handleInputChange('dueDate', new Date(new Date().setDate(new Date().getDate() + 30)).toISOString().slice(0,10))} className="px-2 py-1 border border-slate-600 rounded bg-slate-700 text-gray-300 hover:bg-slate-600">30 días</button>
                  <button onClick={() => handleInputChange('dueDate', new Date(new Date().setDate(new Date().getDate() + 60)).toISOString().slice(0,10))} className="px-2 py-1 border border-slate-600 rounded bg-slate-700 text-gray-300 hover:bg-slate-600">60 días</button>
                  <button onClick={() => handleInputChange('dueDate', new Date(new Date().setDate(new Date().getDate() + 90)).toISOString().slice(0,10))} className="px-2 py-1 border border-slate-600 rounded bg-slate-700 text-gray-300 hover:bg-slate-600">90 días</button>
                  <button onClick={() => handleInputChange('dueDate', '')} className="px-2 py-1 border border-slate-600 rounded bg-slate-700 text-gray-300 hover:bg-slate-600">Sin fecha</button>
                </div>
              </div>
            </div>

            {/* Income Type */}
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-2 md:space-y-0">
              <label className="text-sm font-medium text-gray-400 md:w-24 md:text-right">Tipo de ingreso</label>
              <div className="flex-1 min-w-0">
                <Select
                  value={incomeTypeOptions.find(option => option.value === invoiceData.incomeType)}
                  onChange={(selectedOption) => handleInputChange('incomeType', selectedOption.value)}
                  options={incomeTypeOptions}
                  styles={customStyles}
                  className="w-full"
                  menuPortalTarget={document.body}
                />
              </div>
            </div>

            {/* Client */}
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-2 md:space-y-0">
              <label className="text-sm font-medium text-gray-400 md:w-24 md:text-right">Cliente</label>
              <div className="flex flex-1 flex-col md:flex-row md:items-center md:space-x-2 space-y-2 md:space-y-0">
                <Select
                  options={clientes}
                  value={selectedClient}
                  onChange={setSelectedClient}
                  isClearable
                  placeholder="Selecciona el cliente"
                  styles={customStyles}
                  className="flex-1"
                  menuPortalTarget={document.body}
                />
                <button onClick={() => { console.log('Button + Nuevo Cliente clicked'); setIsModalCrearClienteOpen(true); }} className="text-blue-400 text-sm whitespace-nowrap hover:underline">+ Nuevo Cliente</button>
              </div>
            </div>

            {/* Private Notes */}
            <div className="flex flex-col md:flex-row md:items-start md:space-x-4 space-y-2 md:space-y-0">
              <label className="text-sm font-medium text-gray-400 pt-2 md:w-24 md:text-right">Notas privadas</label>
              <textarea 
                value={invoiceData.privateNotes}
                onChange={(e) => handleInputChange('privateNotes', e.target.value)}
                className="flex-1 border border-slate-600 rounded px-2 py-1 text-sm bg-slate-700 text-gray-300"
                rows="1"
              />
            </div>

            {/* Retention */}
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-2 md:space-y-0">
              <label className="text-sm font-medium text-gray-400 md:w-24 md:text-right">Retención</label>
              <div className="flex-1 min-w-0">
                <Select
                  value={retentionOptions.find(option => option.value === invoiceData.retention)}
                  onChange={(selectedOption) => handleInputChange('retention', selectedOption.value)}
                  options={retentionOptions.filter(o => !o.isGroup)}
                  isOptionDisabled={(option) => option.disabled}
                  styles={customStyles}
                  className="w-full"
                  menuPortalTarget={document.body}
                />
              </div>
            </div>

            {/* Invoice Notes */}
            <div className="flex flex-col md:flex-row md:items-start md:space-x-4 space-y-2 md:space-y-0">
              <label className="text-sm font-medium text-gray-400 pt-2 md:w-24 md:text-right">Notas en la factura</label>
              <div className="flex-1">
                <select className="w-full border border-slate-600 rounded px-3 py-2 text-sm bg-slate-700 text-gray-300 mb-2">
                  <option>Selecciona una nota</option>
                </select>
                <button onClick={() => { console.log('Button Gestionar Notas clicked'); setIsModalNotasOpen(true); console.log('isModalNotasOpen after click:', true); }} className="text-blue-400 text-sm">+ Gestionar información de la nota</button>
                
              </div>
            </div>
          </div>

          <div className="space-y-4">
          </div>
        </div>


      </div>

      {/* Concepts Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-blue-400">Conceptos</h2>
          <button onClick={() => setIsAdvancedEditing(prev => !prev)} className="text-blue-400 text-sm">
            {isAdvancedEditing ? '- Ocultar edición avanzada' : '+ Mostrar edición avanzada'}
          </button>
        </div>

        <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-x-auto">
          <table className="w-full border-collapse" style={{tableLayout: "fixed", minWidth: "640px"}}>
            <thead>
              <tr className="bg-slate-700">
                <th className="border border-slate-600 px-3 py-2 text-left text-sm font-medium text-gray-300" style={{width: "45%"}}>Concepto</th>
                <th className="border border-slate-600 px-3 py-2 text-left text-sm font-medium text-gray-300" style={{width: "15%"}}>Base imponible</th>
                <th className="border border-slate-600 px-3 py-2 text-left text-sm font-medium text-gray-300" style={{width: "8%"}}>Cantidad</th>
                <th className="border border-slate-600 px-3 py-2 text-left text-sm font-medium text-gray-300" style={{width: "12%"}}>% Descuento</th>
                <th className="border border-slate-600 px-3 py-2 text-left text-sm font-medium text-gray-300" style={{width: "10%"}}>IVA</th>
                <th className="border border-slate-600 px-3 py-2 text-left text-sm font-medium text-gray-300" style={{width: "5%"}}></th>
                <th className="border border-slate-600 px-3 py-2 text-left text-sm font-medium text-gray-300" style={{width: "5%"}}></th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.concepts.map((concept, index) => (
                <tr key={concept.id} className="border-b border-slate-700 last:border-0">
                  <td className="border border-slate-700 px-3 py-2">
                    <textarea
                      value={concept.description}
                      onChange={(e) => handleConceptChange(index, 'description', e.target.value)}
                      className="w-full border-0 resize-none text-sm bg-slate-800 text-gray-300"
                      rows="2"
                    />
                    {isAdvancedEditing && (
                      <textarea
                        value={concept.detailedDescription}
                        onChange={(e) => handleConceptChange(index, 'detailedDescription', e.target.value)}
                        className="w-full border-0 resize-none text-sm bg-slate-800 text-gray-300 mt-2"
                        rows="3"
                        placeholder="Descripción detallada de la reparación..."
                      />
                    )}
                  </td>
                  <td className="border border-slate-700 px-3 py-2">
                    <input
                      type="number"
                      value={concept.taxableBase}
                      onChange={(e) => handleConceptChange(index, 'taxableBase', e.target.value)}
                      className="w-full border-0 text-sm bg-slate-800 text-gray-300"
                      step="0.01"
                    />
                  </td>
                  <td className="border border-slate-700 px-3 py-2">
                    <input
                      type="number"
                      value={concept.quantity}
                      onChange={(e) => handleConceptChange(index, 'quantity', e.target.value)}
                      className="w-full border-0 text-sm bg-slate-800 text-gray-300"
                    />
                  </td>
                  <td className="border border-slate-700 px-3 py-2">
                    <input
                      type="number"
                      value={concept.discount}
                      onChange={(e) => handleConceptChange(index, 'discount', e.target.value)}
                      className="w-full border-0 text-sm bg-slate-800 text-gray-300"
                    />
                  </td>
                  <td className="border border-slate-700 px-3 py-2">
                    <select
                      value={concept.vat}
                      onChange={(e) => handleConceptChange(index, 'vat', e.target.value)}
                      className="w-full border-0 text-sm bg-slate-800 text-gray-300"
                    >
                      <option value={21}>21%</option>
                      <option value={10}>10%</option>
                      <option value={4}>4%</option>
                      <option value={0}>0%</option>
                    </select>
                  </td>
                  <td className="border border-slate-700 px-3 py-2">
                    <button onClick={() => deleteConcept(index)} className="text-gray-400 hover:text-red-400"><Trash2 size={16} /></button>
                  </td>
                  <td className="border border-slate-700 px-3 py-2">
                    <div className="relative">
                        <button onClick={() => toggleDropdown(concept.id)} title="Opciones" className="action-btn concept-dropdown-trigger">
                            <MoreVertical size={16} className="text-gray-400" />
                        </button>
                        {openDropdownId === concept.id && (
                            <div ref={dropdownRef} className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 dropdown-menu">
                                <button onClick={() => { deleteConcept(index); setOpenDropdownId(null); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left">Eliminar concepto</button>
                                <button onClick={() => { moveConcepto(concept.id, 'up'); setOpenDropdownId(null); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left">Mover arriba</button>
                                <button onClick={() => { moveConcepto(concept.id, 'down'); setOpenDropdownId(null); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left">Mover abajo</button>
                                <button onClick={() => { duplicateConcept(concept.id); setOpenDropdownId(null); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left">Crear concepto por copia</button>
                            </div>
                        )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button 
          onClick={addConcept}
          className="text-blue-400 text-sm mt-2 hover:underline"
        >
          + Añadir otro concepto
        </button>

        {/* Totals */}
        <div className="flex justify-end mt-6">
          <div className="w-80 bg-slate-700 p-4 rounded-lg border border-slate-600">
            <div className="flex justify-between py-2">
              <span className="text-sm text-gray-400">Subtotal</span>
              <span className="text-sm text-gray-300">{subtotal.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-sm text-gray-400">IVA</span>
              <span className="text-sm text-gray-300">{vatAmount.toFixed(2)} €</span>
            </div>
            {invoiceData.retention !== '0' && ( // Cambiado de 'Sin Retención' a '0'
              <div className="flex justify-between py-2">
                <span className="text-sm text-gray-400">Retención -</span>
                <span className="text-sm text-red-400">{retentionAmount.toFixed(2)} €</span>
              </div>
            )}
            <div className="flex justify-between py-2 font-bold border-t border-slate-600 pt-4">
              <span className="text-sm text-blue-400">TOTAL</span>
              <span className="text-sm text-blue-400">{total.toFixed(2)} €</span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mb-8 pt-4 border-t border-slate-700">
        <button type="button" onClick={handleCreateInvoice} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded text-sm w-full sm:w-auto">
          + Crear factura
        </button>
        <Link to="/facturas" className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded text-sm flex items-center justify-center space-x-2 w-full sm:w-auto">
          <span>←</span>
          <span>Volver al listado</span>
        </Link>
      </div>

      {/* Last Five Invoices Section */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <h3 className="text-lg font-semibold text-white p-4 bg-slate-700 rounded-t-lg border-b border-slate-600">
          Últimas cinco facturas emitidas
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700 border-b border-slate-600">
              <tr>
                <th className="border border-slate-600 px-4 py-3 text-left text-sm font-medium text-gray-300">Número</th>
                <th className="border border-slate-600 px-4 py-3 text-left text-sm font-medium text-gray-300">Fecha de la factura</th>
                <th className="border border-slate-600 px-4 py-3 text-left text-sm font-medium text-gray-300">Cliente</th>
                <th className="border border-slate-600 px-4 py-3 text-left text-sm font-medium text-gray-300">Base imponible</th>
                <th className="border border-slate-600 px-4 py-3 text-left text-sm font-medium text-gray-300">IVA</th>
                <th className="border border-slate-600 px-4 py-3 text-left text-sm font-medium text-gray-300">Retención</th>
                <th className="border border-slate-600 px-4 py-3 text-left text-sm font-medium text-gray-300 flex items-center">
                  Total 
                  <Info className="w-4 h-4 ml-1 text-gray-400" />
                </th>
                <th className="border border-slate-600 px-4 py-3 text-left text-sm font-medium text-gray-300">Estado</th>
                <th className="border border-slate-600 px-4 py-3 text-left text-sm font-medium text-gray-300">Operaciones</th>
              </tr>
            </thead>
            <tbody className="bg-slate-800 divide-y divide-slate-700">
              {lastInvoices.length > 0 ? (
                lastInvoices.map((invoice, index) => (
                  <tr key={invoice.id_factura || index} className="hover:bg-slate-700/50">
                    <td className="px-4 py-3 text-sm text-blue-400 underline cursor-pointer">{invoice.numero_factura}</td>
                    <td className="px-4 py-3 text-sm">{new Date(invoice.fecha_emision).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm">{invoice.nombre_cliente}</td>
                    <td className="px-4 py-3 text-sm">{invoice.base_imponible.toFixed(2)} €</td>
                    <td className="px-4 py-3 text-sm">{invoice.iva.toFixed(2)} €</td>
                    <td className="px-4 py-3 text-sm">{invoice.retencion.toFixed(2)} %</td>
                    <td className="px-4 py-3 text-sm">{invoice.total.toFixed(2)} €</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${invoice.tipo_factura === 'rectificativa' ? 'bg-amber-500/20 text-amber-200' : 'bg-emerald-500/20 text-emerald-200'}`}>
                        {invoice.tipo_factura === 'rectificativa' ? 'Rectificativa' : 'Ordinaria'}
                      </span>
                      <span className="block mt-1 text-[11px] text-gray-400">{invoice.estado_aeat || 'pendiente'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex space-x-1">
                        <button className="p-1 hover:bg-slate-700 rounded text-gray-400" title="Gestionar Cobros" onClick={() => { setSelectedInvoiceIdForCobros(invoice.id_factura); setIsModalGestionCobrosOpen(true); }}><DollarSign size={16} /></button>
                        <button className="p-1 hover:bg-slate-700 rounded text-gray-400" title="Descargar PDF" onClick={() => handleDownloadPdf(invoice.id_factura)}><Download size={16} /></button>
                        <button className="p-1 hover:bg-slate-700 rounded text-emerald-400" title="Compartir PDF por WhatsApp" aria-label={`Compartir factura ${invoice.numero_factura} por WhatsApp`} onClick={() => handleSendInvoiceByWhatsapp(invoice)}><MessageCircle size={16} /></button>
                        <button className="p-1 hover:bg-slate-700 rounded text-blue-300" title="Emitir factura rectificativa" onClick={() => handleEmitirFacturaRectificativa(invoice)}>
                          <span className="text-xs font-semibold">RF</span>
                        </button>
                        <div className="relative">
                          <button onClick={(e) => toggleInvoiceDropdown(invoice.id_factura, e)} className="p-1 hover:bg-slate-700 rounded text-gray-400 invoice-dropdown-trigger">
                            <MoreVertical size={16} />
                          </button>
                          {openInvoiceDropdownId === invoice.id_factura && (
                            <Portal>
                              <div
                                ref={dropdownRef}
                                className="absolute bg-white rounded-md shadow-lg z-10 dropdown-menu"
                                style={{
                                  top: `${dropdownPosition.top}px`,
                                  left: `${dropdownPosition.left}px`,
                                  width: '224px'
                                }}
                              >
                                <button onClick={() => { setSelectedInvoiceIdForCobros(invoice.id_factura); setIsModalGestionCobrosOpen(true); setOpenInvoiceDropdownId(null); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left">Gestionar los cobros</button>
                                <button onClick={() => { handleDownloadPdf(invoice.id_factura); setOpenInvoiceDropdownId(null); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left">Descargar PDF</button>
                                <button onClick={() => { handleSendInvoiceByWhatsapp(invoice); setOpenInvoiceDropdownId(null); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left">Compartir PDF por WhatsApp</button>
                                <button onClick={() => { handleEmitirFacturaRectificativa(invoice); setOpenInvoiceDropdownId(null); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left">Emitir Factura Rectificativa</button>
                                <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-200">
                                  Veri*Factu: {invoice.hash_factura ? 'cadena verificada' : 'pendiente'}
                                </div>
                              </div>
                            </Portal>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-gray-500">No hay facturas recientes para mostrar.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ModalCrearCliente isOpen={isModalCrearClienteOpen} onClose={() => setIsModalCrearClienteOpen(false)} onClienteCreado={handleClienteCreado} />

      <ModalConfigurarFacturas isOpen={isModalConfigurarFacturasOpen} onClose={() => setIsModalConfigurarFacturasOpen(false)} />

      <ModalGestionarNotas 
        isOpen={isModalNotasOpen} 
        onClose={() => setIsModalNotasOpen(false)}
      />
      <CrearSerieNumeracion 
        isOpen={isModalCrearSerieOpen} 
        onClose={() => setIsModalCrearSerieOpen(false)}
        onSerieCreada={handleSerieCreada}
      />

      <ModalGestionCobros
        isOpen={isModalGestionCobrosOpen}
        onClose={() => setIsModalGestionCobrosOpen(false)}
        invoiceId={selectedInvoiceIdForCobros}
      />
    </div>
  );
};

export default InvoiceForm;